import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChange,
  SimpleChanges,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Dropdown } from 'primeng/dropdown';
import {
  FormaPagamento,
  FormaPagamentoConfig,
  FormaPagamentoResult,
  DadosPagamentoDinheiro,
  DadosPagamentoConta,
  DadosPagamentoPrazo,
  DadosPagamentoCartao,
  DadosPagamentoQrCode,
  EstadoQrCodePagamento,
} from './forma-pagamento-dialog.interfaces';
import { toLocalISOString } from '../../utils/date-utils';
import { Moeda } from '../../../enums/moeda.enum';
import { moedasCompativeis, moedasCompativeisInterseccao, podeConverterEntre } from '../../../utils/moeda-pareamento.util';
import { arredondarParaMoeda } from '../../utils/arredondamento-moeda.util';
import { CotacoesDiretas, converterDireto } from '../../utils/moeda-conversao.util';
import {
  FormaPagamentoCardsComponent,
  ValoresRecebidos,
} from '../forma-pagamento-cards/forma-pagamento-cards.component';

@Component({
  selector: 'app-forma-pagamento-dialog',
  templateUrl: './forma-pagamento-dialog.component.html',
  styleUrls: ['./forma-pagamento-dialog.component.scss'],
  providers: [MessageService],
})
/**
 * Dialogo unificado de selecao de forma de pagamento.
 *
 * Visibilidade de cards (modo Dinheiro):
 * - Gate 1 (matriz): apenas as moedas listadas em MOEDAS_COMPATIVEIS para
 *   a moedaPadrao da empresa sao renderizadas como card. Espelha o
 *   validador backend MoedaPareamentoValidator.kt.
 * - Gate 2 (cotacao, defesa em profundidade): card ARS exige adicionalmente
 *   cotPesoGuarani > 0; sem cotacao a conversao retorna zero e o card e
 *   ocultado para evitar inputs com calculos zerados.
 *
 * Edge case: moedaPadrao indefinida ou nao mapeavel -> apenas card PYG
 * (fallback conservador, alinhado com a moeda historica do sistema).
 *
 * Largura do dialog (getDialogStyle) e proporcional ao numero efetivo de
 * cards visiveis (1, 2, 3 ou 4).
 */
export class FormaPagamentoDialogComponent implements OnChanges {

  // ===== INPUTS =====

  /** Controla a visibilidade do dialogo */
  @Input() visible: boolean = false;

  /** Valor total a pagar (subtotal - descontos) */
  @Input() totalAPagar: number = 0;

  /** Configuracao do componente */
  @Input() config: FormaPagamentoConfig = {
    formasPermitidas: ['dinheiro', 'conta', 'prazo'],
    moedaPadrao: 'BRL',
    habilitarMultiMoeda: true,
  };

  /** Lista de contas bancarias (pre-carregada pelo pai) */
  @Input() contas: { label: string; value: any }[] = [];

  /**
   * Contas marcadas para receber tarjeta/QR Code (GET /conta/tarjeta-qrcode).
   * Alimenta APENAS os seletores das formas 'cartao' e 'qrcode' — a forma 'conta'
   * continua usando a lista completa em `contas`.
   */
  @Input() contasTarjetaQrcode: { label: string; value: any }[] = [];

  /**
   * A empresa ja configurou os dados de recebimento (Bancard).
   * Com `false`, a forma QR Code nao gera QR: exibe o aviso e libera somente a
   * selecao de conta, para o operador registrar um recebimento feito por fora.
   */
  @Input() bancardConfigurado: boolean = false;

  /**
   * Conta onde o dinheiro cobrado por QR Code entra, escolhida em Dados de recebimento.
   *
   * O operador não escolhe conta no QR: a cobrança sai pelo cadastro da Bancard da empresa, que
   * aponta para uma conta só. `null` significa que a empresa ainda não vinculou — a tela orienta
   * a fazer isso, porque sem conta não há onde lançar o recebimento.
   */
  @Input() contaQrCode: { id: number; label: string } | null = null;

  /**
   * Estado do QR Code, controlado pelo PAI (quem chama a API e faz o polling).
   * Ver `EstadoQrCodePagamento`.
   */
  @Input() estadoQrCode: EstadoQrCodePagamento = { gerando: false, confirmado: false };

  /**
   * Moeda da nota que esta sendo baixada. Quando informada, o combo de
   * contas (forma "conta") filtra apenas as contas compativeis com essa
   * moeda via matriz de pareamento (espelha MoedaPareamentoValidator no backend).
   * - Nota GUARANI/REAL/DOLAR: mantem comportamento atual (matriz universal).
   * - Nota PESO: apenas contas em GUARANI ou PESO.
   */
  @Input() moedaNota?: Moeda | string | null;

  /**
   * Moeda PADRAO DA EMPRESA (codigo ISO), opcional. Quando informada, aplica
   * um filtro adicional sobre os cards de Dinheiro: a lista visivel passa a
   * ser a intersecao entre MOEDAS_COMPATIVEIS[config.moedaPadrao] (matriz do
   * registro) e MOEDAS_COMPATIVEIS[moedaEmpresa] (matriz da empresa).
   *
   * Justificativa: telas onde a moeda do registro pode diverguir da moeda da
   * empresa (despesa/receita) precisam ocultar moedas que a empresa nao
   * opera (ex.: empresa em REAL nunca pode receber pagamento em PESO,
   * mesmo que o registro seja em GUARANI).
   *
   * Quando omitido, mantem comportamento historico (apenas matriz do
   * registro). Consumidores que usam moeda da empresa como moedaPadrao
   * (PDV, fornecedor-compra, fatura-view, titulos-pessoa) NAO precisam
   * preencher — a intersecao seria idempotente.
   */
  @Input() moedaEmpresa?: 'PYG' | 'BRL' | 'USD' | 'ARS';

  /** Flag de processamento (desabilita botao confirmar) */
  @Input() processando: boolean = false;

  // ===== OUTPUTS =====

  /** Emitido quando a visibilidade muda (two-way binding) */
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Emitido ao confirmar o pagamento */
  @Output() confirmar = new EventEmitter<FormaPagamentoResult>();

  /** Emitido ao cancelar/fechar o dialogo */
  @Output() cancelar = new EventEmitter<void>();

  /**
   * Pede ao PAI que gere a cobranca por QR Code na Bancard.
   *
   * O dialogo nao chama a API: quem faz isso e a tela de venda, via
   * `IntencaoDeRecebimentoService`, devolvendo o resultado por `estadoQrCode`.
   */
  @Output() gerarQrCode = new EventEmitter<void>();

  /**
   * Avisa o PAI que a cobranca por QR em aberto deixou de valer (o operador trocou de forma).
   *
   * Quem fala com a API e a tela de venda; aqui so o aviso, pela mesma regra de nao fazer HTTP
   * fora de services/componentes de pagina.
   */
  @Output() cancelarQrCode = new EventEmitter<void>();

  // ===== ESTADO INTERNO =====

  /** Forma de pagamento selecionada */
  formaPagamentoSelecionada: FormaPagamento = 'dinheiro';

  // Moeda
  private readonly moedaIsoParaEnum: Record<string, string> = {
    'PYG': 'GUARANI',
    'BRL': 'REAL',
    'USD': 'DOLAR',
    'ARS': 'PESO',
  };

  // Cotacoes (defaults seguros)
  cotUSD = 1;
  cotPYG = 7180;
  cotBRL = 5.47;
  /** Cotacao Peso Argentino em Guaranis (1 ARS = N PYG). 0 = peso desabilitado. */
  cotPesoGuarani = 0;
  /** Cotacao direta BRL -> PYG (1 BRL = N PYG). 0 = usar triangulacao via USD. */
  cotRealGuarani = 0;

  /**
   * Valores recebidos por moeda (DTO interno do sub-componente
   * `app-forma-pagamento-cards`). Two-way binding via `[(valores)]` no
   * template. Refletem tambem os campos individuais
   * `valorRecebidoEm{Guarani,Dolar,Real,Peso}` para preservar a API publica
   * de `construirDados()` e validacoes.
   */
  valores: ValoresRecebidos = {
    valorRecebidoEmGuarani: 0,
    valorRecebidoEmReal: 0,
    valorRecebidoEmDolar: 0,
    valorRecebidoEmPeso: 0,
  };

  // Espelhos individuais sincronizados via onValoresChange. Mantidos para
  // compatibilidade com construirDados()/validacoes existentes.
  valorRecebidoEmGuarani: number = 0;
  valorRecebidoEmDolar: number = 0;
  valorRecebidoEmReal: number = 0;
  valorRecebidoEmPeso: number = 0;

  // Troco — valores agora vivem dentro do sub-componente; o dialog so
  // referencia totais agregados (totalTroco abaixo). Mantemos os campos
  // individuais zerados para nao quebrar `construirDados()` enquanto o
  // backend nao consome troco-por-moeda em outros endpoints.
  trocoGuarani: number = 0;
  trocoDolar: number = 0;
  trocoReal: number = 0;
  trocoPeso: number = 0;

  // Totais (atualizados via onTotalConvertidoChange)
  totalPago: number = 0;
  totalTroco: number = 0;
  totalEnviado: number = 0;

  // Indicadores visuais
  corTotalEnviadoDinheiro: string = 'red';
  corTotalEnviado: string = 'black';

  // Conta
  contaSelecionada: any = null;
  informacaoDaTransacao: string = '';

  // Prazo
  numeroParcelas: number = 1;
  titulos: any[] = [];
  dataAtualFormatada: string = '';

  // Cartao
  // Sem bandeira e sem parcelas de proposito: a tela espelha o painel do app, que so pede a conta
  // de destino. O que o operador quiser registrar sobre a transacao vai no campo de texto livre.
  cartaoContaSelecionada: any = null;

  // QR Code
  qrcodeCodigoTransacao: string = '';

  // Expose Math ao template
  Math = Math;

  /**
   * Lista de contas filtradas pela matriz de moedas quando `moedaNota` esta
   * informada. Reaplica a regra backend (MoedaPareamentoValidator) para
   * evitar 422 pos-submit (ex.: nota em ARS so aceita conta em PYG ou ARS).
   *
   * Robustez: `conta.moeda` pode vir como objeto aninhado (`{ enum: 'GUARANI' }`)
   * ou string crua ('GUARANI'). Ambos sao tratados aqui.
   */
  get contasFiltradas(): { label: string; value: any }[] {
    if (!this.moedaNota) {
      return this.contas;
    }
    return this.contas.filter(opt => {
      const moedaContaRaw = opt?.value?.moeda ?? (opt?.value as any)?.moedaDaConta;
      const moedaConta: string | null =
        (typeof moedaContaRaw === 'object' && moedaContaRaw !== null)
          ? (moedaContaRaw.enum || moedaContaRaw.value || null)
          : (typeof moedaContaRaw === 'string' ? moedaContaRaw : null);
      if (!moedaConta) return true; // sem info, nao filtra
      return podeConverterEntre(this.moedaNota as string, moedaConta);
    });
  }

  /**
   * Mesma matriz de moedas de `contasFiltradas`, porem sobre a lista restrita as
   * contas que aceitam tarjeta/QR Code.
   */
  get contasTarjetaQrcodeFiltradas(): { label: string; value: any }[] {
    if (!this.moedaNota) {
      return this.contasTarjetaQrcode;
    }
    return this.contasTarjetaQrcode.filter(opt => {
      const moedaContaRaw = opt?.value?.moeda ?? (opt?.value as any)?.moedaDaConta;
      const moedaConta: string | null =
        (typeof moedaContaRaw === 'object' && moedaContaRaw !== null)
          ? (moedaContaRaw.enum || moedaContaRaw.value || null)
          : (typeof moedaContaRaw === 'string' ? moedaContaRaw : null);
      if (!moedaConta) return true;
      return podeConverterEntre(this.moedaNota as string, moedaConta);
    });
  }

  /**
   * O QR ja foi gerado e esta aguardando (ou ja teve) a confirmacao da Bancard.
   */
  get qrCodeEmAndamento(): boolean {
    return !!this.estadoQrCode?.urlQr || !!this.estadoQrCode?.qrData;
  }

  /**
   * A venda por QR Code so pode ser concluida depois que a Bancard confirmar.
   * Sem os dados de recebimento configurados, o QR nao existe e a conclusao
   * volta a depender apenas da conta escolhida.
   */
  get podeConfirmarQrCode(): boolean {
    if (!this.bancardConfigurado) return true;
    return this.estadoQrCode?.confirmado === true;
  }

    // Guard para evitar confirmacao imediata ao abrir o dialog via Ctrl+Enter
  // (o mesmo evento de keydown pode ser capturado por ambos os HostListeners)
  private dialogAbertoEm: number = 0;

  // ViewChilds para foco
  @ViewChild('cards') cards?: FormaPagamentoCardsComponent;
  @ViewChild('contaDD') contaDD?: Dropdown;
  @ViewChild('numeroParcelasInput') numeroParcelasInput?: ElementRef<HTMLInputElement>;

  /** Bloco do QR gerado — alvo do scroll automatico assim que o codigo aparece. */
  @ViewChild('blocoQrCode') blocoQrCode?: ElementRef<HTMLElement>;

  constructor(
    private messageService: MessageService,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef,
  ) {}

  // ===== HOTKEYS =====

  @HostListener('document:keydown', ['$event'])
  onKeydown(ev: KeyboardEvent): void {
    if (!this.visible) return;

    // Guard: ignorar eventos de Enter/Ctrl+Enter nos primeiros 300ms apos abertura do dialog.
    // Isso evita que o mesmo Ctrl+Enter que abriu o dialog (no VendasComponent)
    // tambem confirme o pagamento imediatamente neste componente.
    const tempoDesdeAbertura = Date.now() - this.dialogAbertoEm;
    if (tempoDesdeAbertura < 300 && (ev.key === 'Enter')) {
      return;
    }

    // Ctrl+Enter: confirmar pagamento (funciona de qualquer lugar dentro do dialog)
    if (ev.ctrlKey && ev.key === 'Enter') {
      ev.preventDefault();
      this.onConfirmar();
      return;
    }

    // Escape: fechar dialog
    if (ev.key === 'Escape') {
      ev.preventDefault();
      this.fecharDialog();
      return;
    }

    // Alt+1: trocar para Dinheiro
    if (ev.altKey && ev.key === '1') {
      if (this.config.formasPermitidas.includes('dinheiro')) {
        ev.preventDefault();
        this.onFormaPagamentoChange('dinheiro');
      }
      return;
    }

    // Alt+2: trocar para Conta
    if (ev.altKey && ev.key === '2') {
      if (this.config.formasPermitidas.includes('conta')) {
        ev.preventDefault();
        this.onFormaPagamentoChange('conta');
      }
      return;
    }

    // Alt+3: trocar para Prazo
    if (ev.altKey && ev.key === '3') {
      if (this.config.formasPermitidas.includes('prazo')) {
        ev.preventDefault();
        this.onFormaPagamentoChange('prazo');
      }
      return;
    }

    // Alt+4: trocar para Cartao
    if (ev.altKey && ev.key === '4') {
      if (this.config.formasPermitidas.includes('cartao')) {
        ev.preventDefault();
        this.onFormaPagamentoChange('cartao');
      }
      return;
    }

    // Alt+5: trocar para QR Code
    if (ev.altKey && ev.key === '5') {
      if (this.config.formasPermitidas.includes('qrcode')) {
        ev.preventDefault();
        this.onFormaPagamentoChange('qrcode');
      }
      return;
    }
  }

  // ===== LIFECYCLE =====

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.config?.cotacoes) {
      this.cotUSD = this.config.cotacoes.cotUSD || 1;
      this.cotPYG = this.config.cotacoes.cotPYG || 7180;
      this.cotBRL = this.config.cotacoes.cotBRL || 5.47;
      // Peso argentino e opcional: sem cotacao, coluna fica oculta via pesoHabilitado
      this.cotPesoGuarani = this.config.cotacoes.cotPesoGuarani || 0;
      // Cotacao direta BRL->PYG: usada para conversao sem triangulacao via USD
      this.cotRealGuarani = this.config.cotacoes.cotRealGuarani || 0;
    }

    if (changes['visible'] && this.visible) {
      this.dialogAbertoEm = Date.now();
      this.dataAtualFormatada = this.formatarDataAtual();
    }

    // Quando a moeda da nota mudar, garante que a conta selecionada ainda
    // esteja compativel via matriz; caso contrario, limpa para o usuario
    // escolher de novo.
    // QR recem-gerado (ou recem-confirmado): trazer para o centro da tela.
    if (changes['estadoQrCode']) {
      this.rolarAteOQrCode(changes['estadoQrCode']);
    }

    if ((changes['moedaNota'] || changes['contas']) && this.contaSelecionada) {
      const aindaValida = this.contasFiltradas.some(opt => opt.value === this.contaSelecionada);
      if (!aindaValida) {
        this.contaSelecionada = null;
      }
    }
  }

  /**
   * Traz o QR para o centro da tela assim que ele passa a existir — e de novo quando o
   * pagamento e confirmado, para o operador ver o aviso sem procurar.
   *
   * Quem rola aqui e o CORPO DO DIALOG, nao a pagina. `scrollIntoView` resolve isso sozinho:
   * ele age sobre todos os containers rolaveis acima do elemento. O `setTimeout` espera o
   * *ngIf renderizar o bloco — sem ele o ViewChild ainda nao existe.
   */
  private rolarAteOQrCode(mudanca: SimpleChange): void {
    const anterior = mudanca.previousValue as EstadoQrCodePagamento | undefined;
    const atual = mudanca.currentValue as EstadoQrCodePagamento | undefined;

    const tinhaQr = !!(anterior?.urlQr || anterior?.qrData);
    const temQr = !!(atual?.urlQr || atual?.qrData);
    const acabouDeAparecer = temQr && !tinhaQr;
    const acabouDeConfirmar = temQr && atual?.confirmado === true && anterior?.confirmado !== true;

    if (!acabouDeAparecer && !acabouDeConfirmar) return;

    setTimeout(() => {
      this.blocoQrCode?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  // ===== DIALOG EVENTS =====

  onShowDialog(): void {
    // Registrar momento de abertura para evitar confirmacao imediata via Ctrl+Enter
    this.dialogAbertoEm = Date.now();

    // Resetar estado ao abrir
    this.resetarCamposPagamento();
    this.formaPagamentoSelecionada = this.config.formasPermitidas[0] || 'dinheiro';

    // Modo dinheiro: forcar prefill da moeda padrao no sub-componente.
    // Setamos um setTimeout para garantir que o ViewChild `cards` esteja
    // disponivel (o *ngIf="formaPagamentoSelecionada === 'dinheiro'" so
    // renderiza o sub-componente apos o ciclo de detecao corrente).
    if (this.formaPagamentoSelecionada === 'dinheiro') {
      setTimeout(() => this.cards?.prefill(), 0);
    }

    if (this.formaPagamentoSelecionada === 'prazo') {
      this.numeroParcelas = 1;
      this.atualizarParcelas();
    }
    this.focusPrimeiroCampoDaForma();
  }

  fecharDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.cancelar.emit();
  }

  onVisibleChange(value: boolean): void {
    this.visible = value;
    this.visibleChange.emit(value);
    if (!value) {
      this.cancelar.emit();
    }
  }

  // ===== DIALOG STYLE =====

  getDialogStyle(): Record<string, string> {
    // Largura proporcional ao numero efetivo de cards visiveis (matriz +
    // pesoHabilitado para ARS). Mantem o comportamento historico para 3 e
    // 4 cards e adiciona o cenario de 2 cards (empresa PESO).
    let width = '940px';
    if (this.config?.habilitarMultiMoeda) {
      const visiveis = new Set(this.moedasVisiveis);
      // Card ARS so renderiza se houver cotacao do Peso (defesa em profundidade)
      if (visiveis.has('ARS') && !this.pesoHabilitado) visiveis.delete('ARS');
      const n = visiveis.size;
      if (n >= 4) width = '1300px';
      else if (n === 3) width = '1100px';
      else if (n === 2) width = '800px';
      else width = '940px'; // 1 card ou estado degenerado: largura padrao
    }
    return {
      width,
      'max-width': '98vw',
    };
  }

  // ===== FORMA DE PAGAMENTO =====

  onFormaPagamentoChange(forma: FormaPagamento): void {
    // Trocar de forma abandona a cobranca por QR: o codigo ja exibido continua pagavel na
    // Bancard, entao o pai precisa cancela-la — nao basta sumir da tela.
    const saindoDoQrCode =
      this.formaPagamentoSelecionada === 'qrcode'
      && forma !== 'qrcode'
      && !!this.estadoQrCode?.intencaoId;

    this.resetarCamposPagamento();
    this.formaPagamentoSelecionada = forma;

    if (saindoDoQrCode) this.cancelarQrCode.emit();

    // Modo dinheiro: forcar prefill da moeda padrao apos a renderizacao
    // condicional do sub-componente (vide onShowDialog()).
    if (forma === 'dinheiro') {
      setTimeout(() => this.cards?.prefill(), 0);
    }

    if (forma === 'prazo') {
      if (!this.numeroParcelas || this.numeroParcelas < 1) this.numeroParcelas = 1;
      this.atualizarParcelas();
    }

    setTimeout(() => this.focusPrimeiroCampoDaForma(), 0);
  }

  private resetarCamposPagamento(): void {
    // Dinheiro — valores e strings sao agora controlados pelo sub-componente.
    // Aqui zeramos o estado local (espelhos) e pedimos reset ao sub-componente.
    this.valorRecebidoEmGuarani = 0;
    this.valorRecebidoEmDolar = 0;
    this.valorRecebidoEmReal = 0;
    this.valorRecebidoEmPeso = 0;
    this.valores = {
      valorRecebidoEmGuarani: 0,
      valorRecebidoEmReal: 0,
      valorRecebidoEmDolar: 0,
      valorRecebidoEmPeso: 0,
    };

    // Troco (espelhos no dialog — sub-componente cuida do estado visual)
    this.trocoGuarani = 0;
    this.trocoDolar = 0;
    this.trocoReal = 0;
    this.trocoPeso = 0;

    // Totais
    this.totalPago = 0;
    this.totalTroco = 0;
    this.totalEnviado = 0;

    // Sub-componente: zera estado interno (strings, troco multi-moeda)
    this.cards?.reset();

    // Conta
    this.contaSelecionada = null;
    this.informacaoDaTransacao = '';

    // Prazo
    this.numeroParcelas = 1;
    this.titulos = [];

    // Cartao
    this.cartaoContaSelecionada = null;

    // QR Code
    this.qrcodeCodigoTransacao = '';
  }

  private focusPrimeiroCampoDaForma(): void {
    if (this.formaPagamentoSelecionada === 'dinheiro') {
      // Focar no input da moeda padrao configurada via API do sub-componente
      const moedaPadrao = this.config.moedaPadrao || 'BRL';
      this.cards?.focusMoeda(moedaPadrao);
    } else if (this.formaPagamentoSelecionada === 'conta') {
      setTimeout(() => { this.contaDD?.show(); this.contaDD?.focus(); }, 0);
    } else if (this.formaPagamentoSelecionada === 'prazo') {
      setTimeout(() => this.numeroParcelasInput?.nativeElement?.focus(), 0);
    }
  }

  // ===== CONFIRMAR =====

  onConfirmar(): void {
    if (this.processando) return;

    // Validacoes
    if (this.formaPagamentoSelecionada === 'conta') {
      if (!this.pagamentoContaValido) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('forma_pagamento.erro_conta_nao_selecionada_titulo'),
          detail: this.translate.instant('forma_pagamento.erro_conta_nao_selecionada_detalhe'),
        });
        return;
      }
    }

    if (this.formaPagamentoSelecionada === 'dinheiro') {
      const totalRecebido = this.calcularTotalRecebidoEmMoedaBase();
      // Tolerancia baseada nas casas decimais da moeda padrao: PYG=1, BRL/USD/ARS=0.01.
      // A conversao direta arredonda cada parcela por etapa, mas a soma de
      // parcelas convertidas ainda pode divergir do total em alguns guaranis.
      // A UI esconde residuos via maximumFractionDigits:0. Sem tolerancia
      // adequada, o usuario ve "Pago: Gs. 50.000" mas recebe erro "valor
      // insuficiente" — paradoxo.
      const tolerancia = 1 / Math.pow(10, this.casasDecimaisDaMoeda());
      if (totalRecebido < this.totalAPagar - tolerancia) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('erro'),
          detail: this.translate.instant('forma_pagamento.erro_valor_insuficiente'),
        });
        return;
      }
    }

    // Bloqueia finalizacao quando o troco informado nao cobre o excedente recebido
    // (pago - troco != total, com tolerancia de 0.01 na moeda base).
    if (this.formaPagamentoSelecionada === 'dinheiro' && !this.pagamentoDinheiroValido) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('forma_pagamento.erro_troco_divergente_titulo'),
        detail: this.translate.instant('forma_pagamento.erro_troco_divergente_detalhe'),
        life: 6000,
      });
      return;
    }

    if (this.formaPagamentoSelecionada === 'prazo') {
      if (!this.pagamentoPrazoValido) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('forma_pagamento.erro_parcelas_divergentes_titulo'),
          detail: this.translate.instant('forma_pagamento.erro_parcelas_divergentes_detalhe'),
          life: 6000,
        });
        return;
      }
    }

    if (this.formaPagamentoSelecionada === 'cartao') {
      if (!this.cartaoContaSelecionada || !this.cartaoContaSelecionada.value || !this.cartaoContaSelecionada.value.id) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('erro'),
          detail: this.translate.instant('forma_pagamento.erro_conta_nao_selecionada'),
        });
        return;
      }
    }

    if (this.formaPagamentoSelecionada === 'qrcode') {
      if (!this.contaQrCode?.id) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('erro'),
          detail: this.translate.instant('forma_pagamento.qrcode_sem_conta_configurada'),
        });
        return;
      }
      // Quem confirma o pagamento por QR e o callback da Bancard, nunca o operador:
      // sem a confirmacao, concluir a venda seria dar baixa em dinheiro que nao entrou.
      if (!this.podeConfirmarQrCode) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('forma_pagamento.qrcode_aguardando_titulo'),
          detail: this.translate.instant('forma_pagamento.qrcode_aguardando_detalhe'),
        });
        return;
      }
    }

    // Construir resultado
    const result: FormaPagamentoResult = {
      forma: this.formaPagamentoSelecionada,
      dados: this.construirDados(),
    };

    this.confirmar.emit(result);
  }

  /**
   * Extrai o codigo da moeda da conta escolhida.
   *
   * O backend serializa enums como objeto (`{ enum: 'GUARANI' }`), mas alguns
   * endpoints devolvem string crua — os dois formatos aparecem em producao.
   */
  private moedaDaConta(opcaoSelecionada: any): string {
    const moeda = opcaoSelecionada?.value?.moeda;
    if (!moeda) return '';
    return typeof moeda === 'object' ? (moeda.enum ?? moeda.value ?? '') : moeda;
  }

  private construirDados(): DadosPagamentoDinheiro | DadosPagamentoConta | DadosPagamentoPrazo | DadosPagamentoCartao | DadosPagamentoQrCode {
    if (this.formaPagamentoSelecionada === 'dinheiro') {
      // Regra unificada: valorLiquido por moeda = valorRecebido - troco da mesma moeda.
      //
      // Cenarios cobertos:
      //   1) Pago exato em uma moeda (200 - 0 = 200) -> entrada de caixa.
      //   2) Pago com troco na MESMA moeda (220 - 20 = 200) -> entrada liquida.
      //   3) Apenas troco naquela moeda (0 - 600 = -600) -> SAIDA de caixa
      //      (cliente nao entregou valor naquela moeda, so RECEBE troco nela).
      //      O valor NEGATIVO sinaliza ao backend que aquela moeda esta saindo do caixa.
      //   4) Moeda nao usada (0 - 0 = 0) -> nenhuma movimentacao.
      //
      // O troco em MOEDA DIFERENTE (excedente cambial) e tratado naturalmente:
      // a moeda que entra vai com sinal positivo e a moeda que sai (troco) vai com sinal
      // negativo. O backend usa a cotacao vigente para conciliar a soma global.
      const valorLiquidoGuarani = (this.valorRecebidoEmGuarani || 0) - (this.trocoGuarani || 0);
      const valorLiquidoDolar   = (this.valorRecebidoEmDolar   || 0) - (this.trocoDolar   || 0);
      const valorLiquidoReal    = (this.valorRecebidoEmReal    || 0) - (this.trocoReal    || 0);
      const valorLiquidoPeso    = (this.valorRecebidoEmPeso    || 0) - (this.trocoPeso    || 0);

      return {
        condicaoDePagamento: 'CONTADO',
        valorRecebidoEmGuarani: valorLiquidoGuarani,
        valorRecebidoEmDolar:   valorLiquidoDolar,
        valorRecebidoEmReal:    valorLiquidoReal,
        valorRecebidoEmPeso:    valorLiquidoPeso,
        trocoGuarani: this.trocoGuarani,
        trocoDolar:   this.trocoDolar,
        trocoReal:    this.trocoReal,
        trocoPeso:    this.trocoPeso,
      } as DadosPagamentoDinheiro;
    }

    if (this.formaPagamentoSelecionada === 'conta') {
      return {
        condicaoDePagamento: 'CONTADO',
        conta_id: this.contaSelecionada?.value?.id || null,
        moedaDaConta: this.contaSelecionada?.value?.moeda?.enum ?? this.contaSelecionada?.value?.moeda ?? '',
        valorRecebidoNaConta: this.totalAPagar,
        informacaoDaTransacao: this.informacaoDaTransacao || '',
      } as DadosPagamentoConta;
    }

    if (this.formaPagamentoSelecionada === 'cartao') {
      return {
        condicaoDePagamento: 'CONTADO',
        conta_id: this.cartaoContaSelecionada?.value?.id || null,
        moedaDaConta: this.moedaDaConta(this.cartaoContaSelecionada),
        informacaoDaTransacao: this.informacaoDaTransacao || '',
      } as DadosPagamentoCartao;
    }

    if (this.formaPagamentoSelecionada === 'qrcode') {
      return {
        condicaoDePagamento: 'CONTADO',
        // A conta do QR nunca é escolhida aqui: vem de Dados de recebimento, e o backend
        // reaplica a mesma regra ao gravar a nota.
        conta_id: this.contaQrCode?.id ?? null,
        moedaDaConta: 'GUARANI',
        intencaoDeRecebimentoId: this.estadoQrCode?.intencaoId ?? null,
        codigoTransacao: this.qrcodeCodigoTransacao || '',
      } as DadosPagamentoQrCode;
    }

    // Prazo
    const titulosFormatados = this.titulos.map((t: any) => {
      const { valorStr, editando, ...titulo } = t;
      // BUG #1: converter vencimento para ISO independente do formato recebido
      titulo.vencimento = this.converterDataParaISO(titulo.vencimento);
      // BUG #3: garantir emissao sempre em formato ISO local sem Z ou milissegundos
      titulo.emissao = this.converterDataParaISO(titulo.emissao);
      return titulo;
    });

    return {
      condicaoDePagamento: 'CREDITO',
      numeroParcelas: this.numeroParcelas,
      titulos: titulosFormatados,
    } as DadosPagamentoPrazo;
  }

  // ===== DINHEIRO — coordenacao com app-forma-pagamento-cards =====

  /** Indica se a coluna de Peso Argentino esta habilitada (cotacao valida).
   *  Mantido aqui para uso em getDialogStyle() (computado antes do
   *  sub-componente ser renderizado). */
  get pesoHabilitado(): boolean {
    return (this.cotPesoGuarani || 0) > 0;
  }

  /**
   * Mapa inverso enum interno -> ISO usado em getDialogStyle().
   * Complementa moedaIsoParaEnum (ISO -> string).
   */
  private readonly moedaEnumParaIso: Record<string, 'PYG' | 'BRL' | 'USD' | 'ARS'> = {
    [Moeda.GUARANI]: 'PYG',
    [Moeda.REAL]:    'BRL',
    [Moeda.DOLAR]:   'USD',
    [Moeda.PESO]:    'ARS',
  };

  /**
   * Conjunto de moedas (codigos ISO) que devem ter card visivel no modo
   * Dinheiro. Replica a logica do sub-componente para sustentar
   * `getDialogStyle()`, que dimensiona o dialog antes do sub-componente
   * estar renderizado.
   *
   * Quando `moedaEmpresa` for informada (opt-in), aplica a intersecao com
   * MOEDAS_COMPATIVEIS[moedaEmpresa] para refletir corretamente a largura
   * do dialog em telas como despesa/receita.
   */
  get moedasVisiveis(): Set<'PYG' | 'BRL' | 'USD' | 'ARS'> {
    const iso = this.config?.moedaPadrao;
    if (!iso) return new Set(['PYG']);

    const enumDaPadrao = this.moedaIsoParaEnum[iso];
    if (!enumDaPadrao) return new Set(['PYG']);

    const enumDaEmpresa = this.moedaEmpresa ? this.moedaIsoParaEnum[this.moedaEmpresa] : undefined;
    const compativeisEnum = enumDaEmpresa
      ? moedasCompativeisInterseccao(enumDaPadrao, enumDaEmpresa)
      : moedasCompativeis(enumDaPadrao);

    if (compativeisEnum.length === 0) return new Set(['PYG']);
    const compativeisIso = compativeisEnum
      .map(m => this.moedaEnumParaIso[m])
      .filter((x): x is 'PYG' | 'BRL' | 'USD' | 'ARS' => !!x);
    return new Set(compativeisIso);
  }

  // ===== VALIDACOES DE PAGAMENTO (consumidas pelo template e onConfirmar) =====
  //
  // Os getters abaixo sao a fonte de verdade do botao "Realizar Venda" e das
  // validacoes em onConfirmar(). Eles permanecem no DIALOG (e nao migraram
  // para o sub-componente) porque dependem de estado proprio do dialog —
  // formaPagamentoSelecionada, contaSelecionada, titulos. O calculo dos
  // trocos em moeda base (calcularTotalTrocoEmMoedaBase) e mantido aqui como
  // helper privado dessas validacoes.

  /**
   * Cotacoes numericas para a conversao direta (espelha
   * `ConversorMoedasUtil.kt`): cotPYG ≡ dolarGuarani, cotBRL ≡ dolarReal,
   * cotRealGuarani ≡ realGuarani, cotPesoGuarani ≡ pesoGuarani.
   */
  private get cotacoes(): CotacoesDiretas {
    return {
      cotPYG: this.cotPYG || 0,
      cotBRL: this.cotBRL || 0,
      cotRealGuarani: this.cotRealGuarani || 0,
      cotPesoGuarani: this.cotPesoGuarani || 0,
    };
  }

  /**
   * Converte com seguranca uma parcela de `origem` para `destino` pelo modelo
   * direto. Antes de converter, valida o pareamento via `podeConverterEntre`
   * (espelha `MoedaPareamentoValidator` do backend). Par incompativel
   * (ARS↔USD/BRL) -> `console.warn` + 0; NUNCA deixa `ConversaoNaoPermitidaError`
   * propagar de um getter usado no template.
   */
  private converterParcela(
    valor: number,
    origem: 'PYG' | 'USD' | 'BRL' | 'ARS',
    destino: 'PYG' | 'USD' | 'BRL' | 'ARS',
  ): number {
    if (!valor) return 0;
    if (origem === destino) return valor;

    const origemEnum = this.moedaIsoParaEnum[origem];
    const destinoEnum = this.moedaIsoParaEnum[destino];
    if (!podeConverterEntre(origemEnum, destinoEnum)) {
      console.warn(
        `[forma-pagamento-dialog] conversao nao permitida ignorada: ${origem}->${destino} (parcela tratada como 0)`,
      );
      return 0;
    }

    return converterDireto(valor, origem, destino, this.cotacoes);
  }

  /**
   * Soma dos trocos digitados em cada moeda, cada um convertido diretamente
   * para a moeda base (config.moedaPadrao). Usado em pagamentoDinheiroValido
   * para fechar a equacao recebido - troco == totalAPagar.
   */
  private calcularTotalTrocoEmMoedaBase(): number {
    const base = (this.config.moedaPadrao || 'BRL') as 'PYG' | 'USD' | 'BRL' | 'ARS';
    return this.converterParcela(this.trocoGuarani || 0, 'PYG', base)
      + this.converterParcela(this.trocoDolar || 0, 'USD', base)
      + this.converterParcela(this.trocoReal || 0, 'BRL', base)
      + this.converterParcela(this.trocoPeso || 0, 'ARS', base);
  }

  /**
   * Retorna true quando o pagamento em Dinheiro esta balanceado.
   *
   * Modelo portado do `vendas-app` (Flutter,
   * `quotation_generic_widget.dart:115-146`): arredonda `(recebido - troco)`
   * e `totalAPagar` para a menor unidade da moeda base e compara igualdade
   * exata. O arredondamento neutraliza resíduos sub-unitários vindos de
   * conversões multi-moeda (ex.: 1299,9975 ou 1300,0031 com cotações ruins)
   * sem precisar de fórmula de tolerância dedicada.
   *
   * Para qualquer forma != 'dinheiro', retorna true (regra nao se aplica).
   * Usado no template ([disabled] do botao) e em onConfirmar().
   */
  get pagamentoDinheiroValido(): boolean {
    if (this.formaPagamentoSelecionada !== 'dinheiro') {
      return true;
    }

    const moedaBase = this.moedaPadrao;
    const totalRecebido = this.calcularTotalRecebidoEmMoedaBase();
    const totalTroco = this.calcularTotalTrocoEmMoedaBase();
    const valorPagoLiquidoArredondado = arredondarParaMoeda(totalRecebido - totalTroco, moedaBase);
    const valorApagarArredondado = arredondarParaMoeda(this.totalAPagar, moedaBase);

    return valorPagoLiquidoArredondado === valorApagarArredondado;
  }

  /**
   * Retorna true quando o pagamento em Conta esta valido (conta selecionada
   * com id). Para qualquer forma != 'conta', retorna true.
   */
  get pagamentoContaValido(): boolean {
    if (this.formaPagamentoSelecionada !== 'conta') {
      return true;
    }
    return !!(this.contaSelecionada && this.contaSelecionada.value && this.contaSelecionada.value.id);
  }

  /**
   * Retorna true quando o pagamento por QR Code esta pronto para concluir: conta escolhida E
   * pagamento ja confirmado pela Bancard. Para qualquer forma != 'qrcode', retorna true.
   *
   * Existe para DESABILITAR o botao. O `onConfirmar` ja recusava a conclusao sem confirmacao,
   * mas o botao continuava com cara de disponivel — o operador clicava e levava um aviso, em vez
   * de ver que ainda estava esperando o cliente pagar.
   */
  get pagamentoQrCodeValido(): boolean {
    if (this.formaPagamentoSelecionada !== 'qrcode') {
      return true;
    }
    const temConta = !!this.contaQrCode?.id;
    return temConta && this.podeConfirmarQrCode;
  }

  /**
   * Retorna true quando o pagamento A Prazo esta valido:
   *   |soma das parcelas - totalAPagar| < tolerancia
   * Para qualquer forma != 'prazo', retorna true.
   */
  get pagamentoPrazoValido(): boolean {
    if (this.formaPagamentoSelecionada !== 'prazo') {
      return true;
    }
    if (!this.titulos || this.titulos.length === 0) {
      return false;
    }
    const somaParcelas = this.titulos.reduce((soma: number, t: any) => soma + (t.valor || 0), 0);
    const tolerancia = 1 / Math.pow(10, this.casasDecimaisDaMoeda());
    return Math.abs(somaParcelas - this.totalAPagar) < tolerancia;
  }

  /**
   * Handler do (valoresChange) do sub-componente. Sincroniza os campos
   * individuais usados por construirDados() / validacoes.
   */
  onValoresChange(v: ValoresRecebidos): void {
    this.valores = v;
    this.valorRecebidoEmGuarani = v.valorRecebidoEmGuarani || 0;
    this.valorRecebidoEmDolar = v.valorRecebidoEmDolar || 0;
    this.valorRecebidoEmReal = v.valorRecebidoEmReal || 0;
    this.valorRecebidoEmPeso = v.valorRecebidoEmPeso || 0;
  }

  /**
   * Handler do (trocosChange) do sub-componente. Sincroniza os trocos
   * individuais consumidos por construirDados() ao montar o DTO de pagamento
   * (formula: valorLiquidoEmM = valorRecebidoEmM - trocoEmM) e recalcula
   * `totalTroco`/`totalEnviado` do footer — como o "Vuelto" agora reflete
   * a SOMA DOS TROCOS DIGITADOS (modelo do mobile, ver `onTotalConvertidoChange`),
   * mudancas nos campos VUELTO precisam reatualizar o footer.
   */
  onTrocosChange(t: { trocoGuarani: number; trocoDolar: number; trocoReal: number; trocoPeso: number }): void {
    this.trocoGuarani = t.trocoGuarani || 0;
    this.trocoDolar   = t.trocoDolar   || 0;
    this.trocoReal    = t.trocoReal    || 0;
    this.trocoPeso    = t.trocoPeso    || 0;
    this.totalTroco = this.calcularTotalTrocoEmMoedaBase();
    this.totalEnviado = this.totalPago - this.totalTroco;
    this.atualizarCorTotalEnviado();
  }

  /**
   * Handler do (totalConvertidoChange) do sub-componente. Atualiza os
   * indicadores `totalPago`, `totalTroco` e `totalEnviado` (este por ultimo
   * porque depende do troco).
   *
   * Modelo portado do `vendas-app` (Flutter, `quotation_generic_controller`
   * `calculateTotals` + `quotation_generic_widget.dart:234-249`):
   *  - "Vuelto" (footer) = SOMA DOS TROCOS DIGITADOS pelo operador (cards),
   *    nao o excedente global (`Pagado - aPagar`). Quando nada e digitado
   *    no campo VUELTO, footer mostra Gs. 0. O excedente devido aparece em
   *    "Vuelto pendente" nos sub-labels de cada card.
   *  - "Total Enviado" = Pagado - Vuelto. Em pagamento exato bate com
   *    totalAPagar; em excedente vai descendo do bruto ate o liquido a
   *    medida que o operador digita os trocos.
   */
  onTotalConvertidoChange(totalNaMoedaPadrao: number): void {
    this.totalPago = totalNaMoedaPadrao;
    this.totalTroco = this.calcularTotalTrocoEmMoedaBase();
    this.totalEnviado = totalNaMoedaPadrao - this.totalTroco;
    this.atualizarCorTotalEnviado();
  }

  /**
   * Recalcula a cor do "Total Enviado" do footer.
   *
   * Verde quando o pagamento está balanceado (`pagamentoDinheiroValido`),
   * vermelho quando ainda falta pagamento, e `#cdcd28` (amarelo) quando há
   * excedente acima do total — alinhada com o gate do botão "Realizar Venda".
   */
  private atualizarCorTotalEnviado(): void {
    this.corTotalEnviadoDinheiro = this.pagamentoDinheiroValido
      ? 'green'
      : (this.totalEnviado < this.totalAPagar ? 'red' : '#cdcd28');
  }

  /**
   * Total recebido convertido para a moeda padrao (validacao em onConfirmar).
   * Usa o ultimo valor agregado emitido pelo sub-componente.
   */
  calcularTotalRecebidoEmMoedaBase(): number {
    return this.totalPago;
  }

  // ===== PARCELAS =====

  adicionarParcela(): void { this.definirNumeroParcelas(this.numeroParcelas + 1); }
  diminuirParcela(): void { this.definirNumeroParcelas(this.numeroParcelas - 1); }
  onNumeroParcelasDigitado(): void { this.definirNumeroParcelas(this.numeroParcelas); }

  private definirNumeroParcelas(n: number): void {
    const novo = Math.max(1, Math.floor(Number(n) || 1));
    this.numeroParcelas = novo;
    this.atualizarParcelas();
  }

  private casasDecimaisDaMoeda(): number {
    return (this.config.moedaPadrao || 'BRL') === 'PYG' ? 0 : 2;
  }

  private dataBaseHoje(): Date {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return hoje;
  }

  atualizarParcelas(): void {
    const dec = this.casasDecimaisDaMoeda();
    const factor = Math.pow(10, dec);
    // BUG #2: usar toFixed para evitar imprecisao IEEE 754 antes de converter para inteiro
    const totalInCents = parseInt(this.totalAPagar.toFixed(dec).replace('.', ''), 10);

    const base = Math.floor(totalInCents / this.numeroParcelas);
    const resto = totalInCents - base * (this.numeroParcelas - 1);
    const valores = Array.from({ length: this.numeroParcelas }, (_, i) =>
      (i === this.numeroParcelas - 1 ? resto : base) / factor
    );

    const baseDate = this.dataBaseHoje();
    const dia = baseDate.getDate();

    const vencs = Array.from({ length: this.numeroParcelas }, (_, i) => {
      const d = new Date(baseDate);
      d.setMonth(d.getMonth() + (i + 1));
      if (d.getDate() !== dia) d.setDate(0);
      return this.formatarDataParaFrontend(d);
    });

    const moedaPadrao = this.config.moedaPadrao || 'BRL';

    this.titulos = valores.map((v, index) => ({
      emissao: toLocalISOString(),
      vencimento: vencs[index],
      saldo: this.totalAPagar - valores.slice(0, index).reduce((s, x) => s + x, 0),
      valor: v,
      valorStr: this.formatarParcelaValor(v),
      numeroParcela: index + 1,
      tipoReceita: 'VENDAS_A_PRAZO',
      tipoOperacaoFinanceira: 'ENTRADA',
      moeda: this.moedaIsoParaEnum[moedaPadrao] || 'GUARANI',
      pessoa_id: null,
      situacaoDeCobranca: 'ABERTO',
    }));
  }

  onParcelaValorInput(titulo: any, ev: Event, index: number): void {
    const input = ev.target as HTMLInputElement;
    const raw = input.value || '';
    const onlyDigits = raw.replace(/\D+/g, '');
    const dec = this.casasDecimaisDaMoeda();

    if (dec === 0) {
      const intVal = onlyDigits ? parseInt(onlyDigits, 10) : 0;
      titulo.valor = intVal;
      titulo.valorStr = intVal ? this.formatarParcelaValor(intVal) : '';
    } else {
      const cents = onlyDigits ? parseInt(onlyDigits, 10) : 0;
      const val = cents / Math.pow(10, dec);
      titulo.valor = val;
      titulo.valorStr = cents ? this.formatarParcelaValor(val) : '';
    }

    input.value = titulo.valorStr;
  }

  recalcularParcelasSubsequentes(index: number): void {
    const valorJaDistribuido = this.titulos.slice(0, index + 1).reduce((sum: number, parcela: any) => sum + parcela.valor, 0);
    const parcelasRestantes = this.titulos.length - (index + 1);
    const dec = this.casasDecimaisDaMoeda();
    const factor = Math.pow(10, dec);
    // BUG #2: usar toFixed para evitar imprecisao IEEE 754 antes de converter para inteiro
    const restante = parseFloat((this.totalAPagar - valorJaDistribuido).toFixed(dec));
    const restanteCents = parseInt(restante.toFixed(dec).replace('.', ''), 10);
    const baseCents = parcelasRestantes > 0 ? Math.floor(restanteCents / parcelasRestantes) : 0;

    for (let i = index + 1; i < this.titulos.length; i++) {
      const isLast = i === this.titulos.length - 1;
      const valorCents = isLast ? (restanteCents - baseCents * (parcelasRestantes - 1)) : baseCents;
      this.titulos[i].valor = valorCents / factor;
      this.titulos[i].valorStr = this.formatarParcelaValor(this.titulos[i].valor);
    }

    this.recalcularSaldos();
  }

  atualizarDatasVencimento(index: number): void {
    if (!this.titulos[index]?.vencimento) return;

    // BUG #1: p-calendar pode retornar Date object ou string DD/MM/YYYY;
    // usar parsearData para garantir conversao correta independente do formato
    const dataBase = this.parsearData(this.titulos[index].vencimento);
    if (!dataBase || isNaN(dataBase.getTime())) return;
    const dia = dataBase.getDate();

    for (let i = index + 1; i < this.titulos.length; i++) {
      const nova = new Date(dataBase);
      nova.setMonth(dataBase.getMonth() + (i - index));
      if (nova.getDate() !== dia) nova.setDate(0);
      this.titulos[i].vencimento = this.formatarDataParaFrontend(nova);
    }
  }

  calcularTotalEnviado(): number {
    const totalEnviado = this.titulos.reduce((soma: number, parcela: any) => soma + parcela.valor, 0);
    this.corTotalEnviado = totalEnviado < this.totalAPagar ? 'red' : totalEnviado === this.totalAPagar ? 'green' : '#cdcd28';
    return totalEnviado;
  }

  private recalcularSaldos(): void {
    let saldoRestante = this.totalAPagar;
    this.titulos.forEach((parcela: any) => {
      parcela.saldo = saldoRestante;
      saldoRestante -= parcela.valor;
    });
  }

  // ===== FLAG URL =====

  getFlagUrlFromConta(conta: { label: string; value: any } | null): string {
    if (!conta) return '';
    const moeda = conta.value?.moeda;
    if (!moeda) return '';
    const moedaStr = this.asEnumString(moeda);
    switch (moedaStr) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }

  private asEnumString(v: any): string {
    if (!v) return '';
    if (typeof v === 'object' && 'enum' in v) return String(v.enum).toUpperCase();
    if (typeof v === 'object' && 'value' in v) return String(v.value).toUpperCase();
    return String(v ?? '').toUpperCase();
  }

  // ===== HELPERS =====

  /**
   * Converte qualquer representacao de data para objeto Date.
   * Suporta: Date object, string DD/MM/YYYY, string ISO (YYYY-MM-DD...).
   */
  private parsearData(valor: any): Date | null {
    if (!valor) return null;
    if (valor instanceof Date) return valor;
    if (typeof valor === 'string') {
      // Formato DD/MM/YYYY
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(valor)) {
        const [dia, mes, ano] = valor.split('/');
        return new Date(parseInt(ano, 10), parseInt(mes, 10) - 1, parseInt(dia, 10));
      }
      // Formato ISO: YYYY-MM-DD... (Date constructor aceita corretamente)
      const d = new Date(valor);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  /**
   * Converte qualquer representacao de data para string ISO local sem Z.
   * Formato de saida: YYYY-MM-DDTHH:mm:ss (esperado pelo backend).
   * BUG #1 e BUG #3: trata Date object, string ISO e string DD/MM/YYYY.
   */
  private converterDataParaISO(valor: any): string {
    const data = this.parsearData(valor);
    if (!data || isNaN(data.getTime())) return '';
    return toLocalISOString(data);
  }

  /** Locale para formatacao de moeda */
  get localeMoeda(): string {
    const moedaPadrao = this.config.moedaPadrao || 'BRL';
    if (moedaPadrao === 'PYG') return 'es-PY';
    if (moedaPadrao === 'USD') return 'en-US';
    if (moedaPadrao === 'ARS') return 'es-AR';
    return 'pt-BR';
  }

  /** Digits pipe para a moeda padrao */
  get digitsMoeda(): string {
    return (this.config.moedaPadrao || 'BRL') === 'PYG' ? '1.0-0' : '1.2-2';
  }

  /** Moeda padrao da config */
  get moedaPadrao(): 'BRL' | 'PYG' | 'USD' | 'ARS' {
    return this.config.moedaPadrao || 'BRL';
  }

  private formatarDataAtual(): string {
    const hoje = new Date();
    const dia = String(hoje.getDate()).padStart(2, '0');
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const ano = hoje.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  private formatarDataParaFrontend(data: Date): string {
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  formatarParcelaValor(valor: number): string {
    if (!valor && valor !== 0) return '';
    const dec = this.casasDecimaisDaMoeda();
    return valor.toLocaleString(this.localeMoeda, { minimumFractionDigits: dec, maximumFractionDigits: dec });
  }
}
